// DietPlans.js
import React, { useState } from 'react';
import { FaCalendarAlt, FaSearch, FaInfoCircle, FaUtensils, FaUser, FaPlus, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import './DietPlans.css';
import { useKitchenData } from './Context/KitchenDataContext';
import KitchenSidebar from './KitchenSidebar';

// Main Diet Plans Page Component
const DietPlansPage = () => {
  const { dietPlans, addDietPlan, deleteDietPlan } = useKitchenData();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handleAddDietPlan = (planData) => {
    if (!selectedPatient) return;

    const newPlan = {
      ...planData,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      patientRoom: selectedPatient.room
    };

    addDietPlan(newPlan);
    setShowAddForm(false);
    setSelectedPatient(null);
  };

  const handleDeletePlan = (planId) => {
    if (window.confirm('Are you sure you want to delete this diet plan?')) {
      deleteDietPlan(planId);
    }
  };
  return (
    <div className="kitchen-layout">
      <KitchenSidebar />
      <div className="kitchen-root col-10">
        <div className="dp-container">
          <div className="dp-header">
            <div className="dp-header-content">
              <h1 className="dp-page-title">Patient Diet Plans</h1>
              <p className="dp-page-subtitle">Thursday, September 18</p>
            </div>
            {dietPlans.length === 0 && !showAddForm ? (
              <div className="dp-empty-state">
                <div className="dp-empty-state-icon">
                  <FaUtensils />
                </div>
                <p className="dp-empty-state-text">No diet plans added yet.</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="dp-btn-primary"
                >
                  <FaPlus className="dp-btn-icon" />
                  Add New Diet Plan
                </button>
              </div>
            ) : (
              <>
                {!showAddForm && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="dp-btn-primary dp-add-plan-btn"
                  >
                    <FaPlus className="dp-btn-icon" />
                    Add New Diet Plan
                  </button>
                )}

                {showAddForm && (
                  <AddDietPlanForm
                    selectedPatient={selectedPatient}
                    onPatientSelect={setSelectedPatient}
                    onSave={handleAddDietPlan}
                    onCancel={() => { setShowAddForm(false); setSelectedPatient(null); }}
                  />
                )}
                <div className="dp-grid">
                  {dietPlans.map(plan => (
                    <DietPlanCard
                      key={plan.id}
                      plan={plan}
                      onDelete={() => handleDeletePlan(plan.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Patient Search Component
const PatientSearch = ({ selectedPatient, onPatientSelect }) => {
  const { patients } = useKitchenData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredPatients, setFilteredPatients] = useState(patients);

  React.useEffect(() => {
    if (searchTerm) {
      const filtered = patients.filter(patient =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.room.includes(searchTerm) ||
        patient.condition.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
      setShowDropdown(true);
    } else {
      setFilteredPatients(patients);
      setShowDropdown(false);
    }
  }, [searchTerm, patients]);

  const handlePatientSelect = (patient) => {
    onPatientSelect(patient);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const clearSelectedPatient = () => {
    onPatientSelect(null);
    setSearchTerm('');
  };

  return (
    <div className="dp-patient-search">
      <label className="dp-form-label">Select Patient *</label>

      {selectedPatient ? (
        <div className="dp-selected-patient">
          <div className="dp-patient-info">
            <div className="dp-patient-avatar">
              <FaUser />
            </div>
            <div className="dp-patient-details">
              <div className="dp-patient-name">{selectedPatient.name}</div>
              <div className="dp-patient-meta">
                Room {selectedPatient.room} • {selectedPatient.condition} • Age {selectedPatient.age}
              </div>
            </div>
          </div>
          <button
            onClick={clearSelectedPatient}
            className="dp-btn-clear-patient"
            type="button"
          >
            <FaTimes />
          </button>
        </div>
      ) : (
        <div className="dp-search-container">
          <div className="dp-input-group">
            <FaSearch className="dp-input-icon" />
            <input
              type="text"
              placeholder="Search by patient name, room number, or condition..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              className="dp-form-input"
            />
          </div>

          {showDropdown && (
            <div className="dp-patients-dropdown">
              {filteredPatients.length > 0 ? (
                filteredPatients.map(patient => (
                  <div
                    key={patient.id}
                    onClick={() => handlePatientSelect(patient)}
                    className="dp-patient-option"
                  >
                    <div className="dp-patient-avatar">
                      <FaUser />
                    </div>
                    <div className="dp-patient-details">
                      <div className="dp-patient-name">{patient.name}</div>
                      <div className="dp-patient-meta">
                        Room {patient.room} • {patient.condition} • Age {patient.age}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="dp-no-patients">
                  No patients found matching "{searchTerm}"
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Add Diet Plan Form Component
const AddDietPlanForm = ({ selectedPatient, onPatientSelect, onSave, onCancel }) => {
  const { menuItems } = useKitchenData();
  const [formData, setFormData] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    meals: []
  });

  const [currentMeal, setCurrentMeal] = useState({
    foodCode: '',
    mealType: 'Breakfast',
    time: '08:00',
    scheduleDays: []
  });

  const [showMealForm, setShowMealForm] = useState(false);

  const handleSave = () => {
    if (!selectedPatient) {
      alert('Please select a patient first');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="dp-add-form">
      <div className="dp-form-header">
        <h2 className="dp-form-title">Add New Diet Plan</h2>
        <button onClick={onCancel} className="dp-btn-close">
          <FaTimes />
        </button>
      </div>

      <PatientSearch
        selectedPatient={selectedPatient}
        onPatientSelect={onPatientSelect}
      />

      <div className="dp-form-grid">
        <div className="dp-form-group">
          <label className="dp-form-label">Start Date</label>
          <div className="dp-input-group">
            <FaCalendarAlt className="dp-input-icon" />
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="dp-form-input"
            />
          </div>
        </div>

        <div className="dp-form-group">
          <label className="dp-form-label">End Date (2 Weeks Default)</label>
          <div className="dp-input-group">
            <FaCalendarAlt className="dp-input-icon" />
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="dp-form-input"
            />
          </div>
        </div>
      </div>

      <div className="dp-form-group">
        <label className="dp-form-label">Notes</label>
        <textarea
          placeholder="Any specific diet restrictions, preferences, or goals..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="dp-form-textarea"
          rows={4}
        />
      </div>

      {!showMealForm ? (
        <button
          onClick={() => setShowMealForm(true)}
          className="dp-btn-secondary"
        >
          <FaPlus className="dp-btn-icon" />
          Quick Add Food by Code
        </button>
      ) : (
        <QuickAddMealForm
          meal={currentMeal}
          onChange={setCurrentMeal}
          onAddMeal={(meal) => {
            setFormData({
              ...formData,
              meals: [...formData.meals, { ...meal, id: Date.now() }]
            });
            setCurrentMeal({
              foodCode: '',
              mealType: 'Breakfast',
              time: '08:00',
              scheduleDays: []
            });
          }}
          onCancel={() => setShowMealForm(false)}
        />
      )}

      {formData.meals.length > 0 && (
        <div className="dp-meals-section">
          <h3 className="dp-section-title">Added Meals</h3>
          <div className="dp-meals-list">
            {formData.meals.map(meal => (
              <div key={meal.id} className="dp-meal-item">
                <div className="dp-meal-info">
                  <span className="dp-meal-code">{meal.foodCode}</span>
                  <span className="dp-meal-details">
                    ({meal.mealType} at {meal.time}) - {menuItems[meal.foodCode]?.name || 'Unknown'}
                  </span>
                </div>
                <button
                  onClick={() => setFormData({
                    ...formData,
                    meals: formData.meals.filter(m => m.id !== meal.id)
                  })}
                  className="dp-btn-remove"
                >
                  <FaTimes />
                </button>
                {meal.scheduleDays.length > 0 && (
                  <div className="dp-meal-schedule">
                    Days: {meal.scheduleDays.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="dp-form-actions">
        <button onClick={onCancel} className="dp-btn-cancel">
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={`dp-btn-save ${!selectedPatient ? 'dp-btn-disabled' : ''}`}
          disabled={!selectedPatient}
        >
          Save Diet Plan
        </button>
      </div>
    </div>
  );
};

// Quick Add Meal Form Component
const QuickAddMealForm = ({ meal, onChange, onAddMeal, onCancel }) => {
  const { menuItems } = useKitchenData();
  const [selectedDays, setSelectedDays] = useState([]);
  const [schedulePattern, setSchedulePattern] = useState('Custom');

  const schedulePatterns = [
    { name: 'Custom', days: [] },
    { name: 'All Days', days: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] },
    { name: 'Odd Days', days: [1, 3, 5, 7, 9, 11, 13] },
    { name: 'Even Days', days: [2, 4, 6, 8, 10, 12, 14] },
    { name: 'Weekdays', days: [1, 2, 3, 4, 5, 8, 9, 10, 11, 12] },
    { name: 'Weekends', days: [6, 7, 13, 14] },
    { name: 'Week 1', days: [1, 2, 3, 4, 5, 6, 7] },
    { name: 'Week 2', days: [8, 9, 10, 11, 12, 13, 14] }
  ];

  // Define the allowed time options
  const timeOptions = [
    { value: '08:00', label: '08:00 AM' },
    { value: '10:30', label: '10:30 AM' },
    { value: '13:00', label: '01:00 PM' },
    { value: '16:00', label: '04:00 PM' },
    { value: '20:00', label: '08:00 PM' }
  ];

  const handlePatternChange = (patternName) => {
    setSchedulePattern(patternName);
    const pattern = schedulePatterns.find(p => p.name === patternName);
    setSelectedDays(pattern.days);
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    );
    setSchedulePattern('Custom');
  };

  const handleAddMeal = () => {
    onAddMeal({
      ...meal,
      scheduleDays: selectedDays
    });
    setSelectedDays([]);
    setSchedulePattern('Custom');
  };

  return (
    <div className="dp-quick-add-meal">
      <div className="dp-meal-form-header">
        <h3 className="dp-meal-form-title">Quick Add Food by Code</h3>
        <button onClick={onCancel} className="dp-btn-close-small">
          <FaTimes />
        </button>
      </div>

      <div className="dp-meal-form-grid">
        <div className="dp-form-group">
          <label className="dp-form-label">Food Code (3 letters)</label>
          <div className="dp-input-group">
            <FaSearch className="dp-input-icon" />
            <select
              value={meal.foodCode}
              onChange={(e) => onChange({ ...meal, foodCode: e.target.value })}
              className="dp-form-input"
            >
              <option value="">Select Food Item</option>
              {Object.values(menuItems).map(item => (
                <option key={item.code} value={item.code}>
                  {item.code} - {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="dp-form-group">
          <label className="dp-form-label">Meal Type</label>
          <select
            value={meal.mealType}
            onChange={(e) => onChange({ ...meal, mealType: e.target.value })}
            className="dp-form-select"
          >
            <option>Breakfast</option>
            <option>Lunch</option>
            <option>Dinner</option>
            <option>Snack</option>
          </select>
        </div>
      </div>

      <div className="dp-form-group">
        <label className="dp-form-label">
          Time
          <FaInfoCircle className="dp-info-icon" />
        </label>
        <select
          value={meal.time}
          onChange={(e) => onChange({ ...meal, time: e.target.value })}
          className="dp-form-select"
        >
          {timeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="dp-schedule-section">
        <label className="dp-form-label">Select Schedule Pattern</label>
        <div className="dp-pattern-buttons">
          {schedulePatterns.map(pattern => (
            <button
              key={pattern.name}
              onClick={() => handlePatternChange(pattern.name)}
              className={`dp-pattern-btn ${schedulePattern === pattern.name ? 'dp-active' : ''}`}
            >
              {pattern.name}
            </button>
          ))}
        </div>
      </div>

      <div className="dp-days-section">
        <div className="dp-days-header">
          <span className="dp-days-count">Selected Days: {selectedDays.length}</span>
          <div className="dp-days-actions">
            <button
              onClick={() => setSelectedDays([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14])}
              className="dp-btn-link"
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedDays([])}
              className="dp-btn-link"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="dp-days-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(day => (
            <button
              key={day}
              onClick={() => toggleDay(day)}
              className={`dp-day-btn ${selectedDays.includes(day) ? 'dp-selected' : ''}`}
            >
              {day}
            </button>
          ))}
        </div>
      </div>

      <div className="dp-meal-form-actions">
        <button onClick={onCancel} className="dp-btn-cancel">
          Cancel
        </button>
        <button
          onClick={handleAddMeal}
          disabled={!meal.foodCode || selectedDays.length === 0}
          className="dp-btn-primary"
        >
          Plan This Meal
        </button>
      </div>
    </div>
  );
};

// Diet Plan Card Component
const DietPlanCard = ({ plan, onDelete }) => {
  const { getMenuItemByCode } = useKitchenData();

  return (
    <div className="dp-diet-plan-card">
      <div className="dp-card-header">
        <div className="dp-card-info">
          <div className="dp-patient-header">
            <div className="dp-patient-avatar-small">
              <FaUser />
            </div>
            <div>
              <h3 className="dp-card-title">{plan.patientName}</h3>
              <p className="dp-card-patient-info">Room {plan.patientRoom}</p>
            </div>
          </div>
          <p className="dp-card-subtitle">
            {plan.startDate} to {plan.endDate} • {plan.meals.length} meals planned
          </p>
        </div>
        <div className="dp-card-actions">
          <button className="dp-btn-edit">
            <FaEdit />
          </button>
          <button className="dp-btn-delete" onClick={onDelete}>
            <FaTrash />
          </button>
        </div>
      </div>

      {plan.notes && (
        <div className="dp-card-notes">
          <p>{plan.notes}</p>
        </div>
      )}

      {plan.meals.length > 0 && (
        <div className="dp-card-meals">
          <h4 className="dp-meals-title">Planned Meals:</h4>
          <div className="dp-meals-preview">
            {plan.meals.slice(0, 3).map(meal => {
              const menuItem = getMenuItemByCode(meal.foodCode);
              return (
                <div key={meal.id} className="dp-meal-preview">
                  {meal.foodCode} - {menuItem?.name || 'Unknown'} ({meal.mealType} at {meal.time})
                </div>
              );
            })}
            {plan.meals.length > 3 && (
              <div className="dp-meals-more">
                +{plan.meals.length - 3} more meals...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DietPlansPage;